import React, { useState } from 'react';
import { 
  ChevronRight, ChevronDown, ArrowUpRight, ArrowDownRight, Clock, Target,
  Utensils, Car, Zap, Tv, ShoppingBag, Heart, GraduationCap, PiggyBank, MoreHorizontal,
  Sparkles, X, Gift
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useBudget } from '@/contexts/BudgetContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { TrialBanner } from '@/components/subscription/TrialBanner';
import { PowerPackModal } from '@/components/subscription/PowerPackModal';

// Category config - with fallback colors for custom categories
const CATEGORY_CONFIG = {
  'Food & Dining': { icon: Utensils, color: '#f97316' },
  'Transportation': { icon: Car, color: '#3b82f6' },
  'Utilities': { icon: Zap, color: '#eab308' },
  'Entertainment': { icon: Tv, color: '#8b5cf6' },
  'Shopping': { icon: ShoppingBag, color: '#ec4899' },
  'Healthcare': { icon: Heart, color: '#ef4444' },
  'Education': { icon: GraduationCap, color: '#06b6d4' },
  'Savings': { icon: PiggyBank, color: '#22c55e' },
  'Investment': { icon: PiggyBank, color: '#10b981' },
  'Salary': { icon: MoreHorizontal, color: '#22c55e' },
  'Freelance': { icon: MoreHorizontal, color: '#14b8a6' },
  'Other': { icon: MoreHorizontal, color: '#6b7280' }
};

// Generate a color for unknown categories
const getCategoryColor = (category) => {
  if (CATEGORY_CONFIG[category]) return CATEGORY_CONFIG[category].color;
  // Generate consistent color from category name
  const colors = ['#f97316', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4', '#14b8a6', '#f59e0b', '#84cc16'];
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Transaction row
function TransactionRow({ transaction, currencySymbol }) {
  const isExpense = transaction.type === 'expense';
  const config = CATEGORY_CONFIG[transaction.category] || CATEGORY_CONFIG['Other'];
  const Icon = config.icon;
  const color = config.color;
  
  return (
    <div className="flex items-center gap-3 py-3">
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: color + '20' }}
      >
        <Icon className="w-5 h-5" style={{ color: color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{transaction.description || transaction.category}</p>
        <p className="text-xs text-muted-foreground">{transaction.category}</p>
      </div>
      <span className={`text-sm font-semibold ${isExpense ? 'text-red-500' : 'text-emerald-500'}`}>
        {isExpense ? '-' : '+'}{currencySymbol}{transaction.amount.toFixed(0)}
      </span>
    </div>
  );
}

// Budget progress bar - improved design with expandable details
function BudgetProgressItem({ category, spent, limit, currencySymbol, transactions = [], onViewCategory }) {
  const [expanded, setExpanded] = useState(false);
  const hasLimit = limit > 0;
  const percentage = hasLimit ? Math.min((spent / limit) * 100, 100) : 100;
  const isOver = hasLimit && spent > limit;
  const remaining = hasLimit ? Math.max(limit - spent, 0) : 0;
  const Icon = CATEGORY_CONFIG[category]?.icon || MoreHorizontal;
  const color = getCategoryColor(category);
  
  // Get recent transactions for this category (max 5)
  const categoryTransactions = transactions
    .filter(t => t.category === category && t.type === 'expense')
    .slice(0, 5);
  const txCount = transactions.filter(t => t.category === category && t.type === 'expense').length;
  
  return (
    <div className="rounded-lg transition-all duration-200">
      <div 
        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
          expanded ? 'bg-muted/50' : 'hover:bg-muted/30'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div 
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: color + '15' }}
        >
          <Icon className="w-4 h-4" style={{ color: color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium truncate">{category}</span>
              {expanded ? (
                <ChevronDown className="w-3 h-3 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-3 h-3 text-muted-foreground" />
              )}
            </div>
            {hasLimit ? (
              <span className={`text-[10px] font-semibold ${isOver ? 'text-red-500' : 'text-emerald-500'}`}>
                {isOver ? 'Over!' : `${currencySymbol}${remaining.toFixed(0)} left`}
              </span>
            ) : (
              <span className="text-[10px] font-medium text-muted-foreground">
                {currencySymbol}{spent.toFixed(0)} spent
              </span>
            )}
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: hasLimit ? `${percentage}%` : '100%',
                backgroundColor: isOver ? '#ef4444' : color,
                opacity: hasLimit ? 1 : 0.6
              }}
            />
          </div>
        </div>
        {hasLimit && (
          <div className="text-right flex-shrink-0 w-12">
            <p className="text-xs font-bold" style={{ color: isOver ? '#ef4444' : color }}>
              {Math.round(percentage)}%
            </p>
          </div>
        )}
      </div>
      
      {/* Expanded Details */}
      {expanded && (
        <div className="mt-2 ml-12 mr-2 pb-2 space-y-2 animate-fade-in">
          {/* Summary Stats */}
          <div className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg">
            <div className="flex-1">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Spent</p>
              <p className="text-sm font-bold" style={{ color }}>{currencySymbol}{spent.toFixed(2)}</p>
            </div>
            {hasLimit && (
              <>
                <div className="w-px h-8 bg-border" />
                <div className="flex-1">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Budget</p>
                  <p className="text-sm font-bold">{currencySymbol}{limit.toFixed(2)}</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div className="flex-1">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    {isOver ? 'Over By' : 'Remaining'}
                  </p>
                  <p className={`text-sm font-bold ${isOver ? 'text-red-500' : 'text-emerald-500'}`}>
                    {currencySymbol}{Math.abs(limit - spent).toFixed(2)}
                  </p>
                </div>
              </>
            )}
            <div className="w-px h-8 bg-border" />
            <div className="flex-1">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Transactions</p>
              <p className="text-sm font-bold">{txCount}</p>
            </div>
          </div>
          
          {/* Recent Transactions */}
          {categoryTransactions.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide px-1">Recent Transactions</p>
              {categoryTransactions.map((tx, idx) => (
                <div 
                  key={tx.id || idx}
                  className="flex items-center justify-between px-2 py-1.5 bg-background rounded-md text-xs"
                >
                  <div className="flex-1 min-w-0">
                    <span className="truncate">{tx.description || category}</span>
                    <span className="text-muted-foreground ml-2">
                      {new Date(tx.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <span className="font-semibold text-red-500 ml-2">
                    -{currencySymbol}{tx.amount.toFixed(2)}
                  </span>
                </div>
              ))}
              {txCount > 5 && (
                <p className="text-[10px] text-center text-muted-foreground pt-1">
                  +{txCount - 5} more transactions
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const Dashboard = ({ onViewAllTransactions }) => {
  const { getMonthTransactions, getMonthTotals, budgetGoals, formatMoneyCompact, currencySymbol } = useBudget();
  const { bonusScans, bonusTransactions, tier } = useSubscription();
  const [budgetExpanded, setBudgetExpanded] = useState(true);
  const [showPowerPackModal, setShowPowerPackModal] = useState(false);
  const [promoDismissed, setPromoDismissed] = useState(() => {
    return localStorage.getItem('powerpack_promo_dismissed') === 'true';
  });
  
  const today = new Date();
  const { income, expenses, balance } = getMonthTotals(today);
  // Use getMonthTransactions to get converted amounts
  const monthTransactions = getMonthTransactions(today);
  const recentTransactions = monthTransactions.slice(0, 5);
  
  // Build transaction rows
  const txRows = [];
  for (let i = 0; i < recentTransactions.length; i++) {
    txRows.push(<TransactionRow key={i} transaction={recentTransactions[i]} currencySymbol={currencySymbol} />);
  }

  // Calculate spending by category for current month (using converted amounts)
  const categorySpending = {};
  for (let i = 0; i < monthTransactions.length; i++) {
    const t = monthTransactions[i];
    if (t.type !== 'expense') continue;
    
    if (!categorySpending[t.category]) {
      categorySpending[t.category] = 0;
    }
    categorySpending[t.category] += t.amount;
  }

  // Build budget progress items - ONLY for categories with actual spending this month
  const budgetItems = [];
  const usedCategories = Object.keys(categorySpending).filter(cat => categorySpending[cat] > 0);
  
  // Sort by spending amount (highest first)
  usedCategories.sort((a, b) => (categorySpending[b] || 0) - (categorySpending[a] || 0));
  
  for (let i = 0; i < usedCategories.length; i++) {
    const catName = usedCategories[i];
    const limit = budgetGoals[catName] || 0;
    const spent = categorySpending[catName] || 0;
    
    // Show category if it has spending (with or without budget goal)
    budgetItems.push(
      <BudgetProgressItem 
        key={catName}
        category={catName}
        spent={spent}
        limit={limit}
        currencySymbol={currencySymbol}
        transactions={monthTransactions}
      />
    );
  }

  const isPositive = balance >= 0;
  const monthName = today.toLocaleDateString('en-US', { month: 'long' });

  // Handle upgrade click
  const handleUpgrade = () => {
    window.dispatchEvent(new CustomEvent('navigate', { detail: 'pricing' }));
  };

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Trial Banner */}
      <TrialBanner onUpgrade={handleUpgrade} />
      
      {/* Power Pack Promo Banner - Show for non-Elite users who haven't purchased packs */}
      {!promoDismissed && tier !== 'elite' && bonusScans === 0 && bonusTransactions === 0 && (
        <Card className="relative overflow-hidden border-0 bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-rose-500/20">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTJoLTJ2Mmgyek0zMCAzNmgtMnYtMmgydjJ6bTAtNHYtMmgtMnYyaDJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
          <button 
            onClick={() => {
              setPromoDismissed(true);
              localStorage.setItem('powerpack_promo_dismissed', 'true');
            }}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-black/10 transition-colors z-10"
          >
            <X className="w-4 h-4 text-amber-800/60" />
          </button>
          <CardContent className="p-4 relative">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/30">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="font-bold text-amber-900 dark:text-amber-100">Power Packs</h3>
                  <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 text-[10px] font-bold">
                    SAVE 25%+
                  </span>
                </div>
                <p className="text-xs text-amber-800/80 dark:text-amber-200/80">
                  Get scans + transactions together at a discount!
                </p>
              </div>
              <Button 
                size="sm"
                onClick={() => setShowPowerPackModal(true)}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg flex-shrink-0"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                View Packs
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Balance Hero Card */}
      <Card className="border-0 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent shadow-lg overflow-hidden">
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground mb-1">{monthName} Balance</p>
          <div className="flex items-baseline gap-2 mb-4">
            <span className={`text-4xl font-bold ${isPositive ? 'text-foreground' : 'text-red-500'}`}>
              {isPositive ? '' : '-'}{currencySymbol}{Math.abs(balance).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>
          
          <div className="flex gap-3">
            <div className="flex items-center gap-2 bg-emerald-500/10 rounded-full px-3 py-1.5">
              <ArrowUpRight className="w-4 h-4 text-emerald-500" />
              <span className="text-sm font-medium text-emerald-600">{formatMoneyCompact(income)}</span>
            </div>
            <div className="flex items-center gap-2 bg-red-500/10 rounded-full px-3 py-1.5">
              <ArrowDownRight className="w-4 h-4 text-red-500" />
              <span className="text-sm font-medium text-red-600">{formatMoneyCompact(expenses)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Spending by Category - Collapsible */}
      {budgetItems.length > 0 && (
        <Card className="border border-border/50 overflow-hidden">
          <div 
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => setBudgetExpanded(!budgetExpanded)}
          >
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold">Spending by Category</span>
              <span className="text-xs text-muted-foreground">({budgetItems.length})</span>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs h-6 px-2 text-muted-foreground hover:text-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  window.dispatchEvent(new CustomEvent('navigate', { detail: 'settings' }));
                }}
              >
                Set Goals
              </Button>
              {budgetExpanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
          </div>
          {budgetExpanded && (
            <div className="px-4 pb-4 space-y-1">
              {budgetItems}
            </div>
          )}
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card 
          className="border border-border/50 cursor-pointer hover:bg-muted/30 transition-colors"
          onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'subscriptions' }))}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Subscriptions</p>
                <p className="text-lg font-bold">Manage</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="border border-border/50 cursor-pointer hover:bg-muted/30 transition-colors"
          onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'savings' }))}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Savings Tips</p>
                <p className="text-lg font-bold">View Tips</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold">Recent Activity</h3>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs h-7 text-muted-foreground"
            onClick={onViewAllTransactions}
          >
            See All <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        
        <Card className="border border-border/50">
          <CardContent className="p-4">
            {txRows.length > 0 ? (
              <div className="divide-y divide-border/50">
                {txRows}
              </div>
            ) : (
              <div className="py-8 text-center">
                <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-50" />
                <p className="text-sm text-muted-foreground">No transactions yet</p>
                <p className="text-xs text-muted-foreground">Tap + to add your first one</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Power Pack Modal */}
      <PowerPackModal 
        open={showPowerPackModal} 
        onOpenChange={setShowPowerPackModal} 
      />
    </div>
  );
};
